/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState, useMemo } from "react";
import { Modal, Radio, Form, Input } from "antd";
import PropTypes from "prop-types";

/**
 * Función helper para determinar el valor inicial de 'option'
 * basado en 'action' y 'parentAction'.
 */
const getInitialOption = (action, parentAction) => {
  if (action === "problema") {
    return parentAction === "iniciar" ? "punto_recogida" : "punto_destino";
  }
  return "recomendada";
};

const OptionModal = ({
  open,
  onCancel,
  onSubmit,
  title,
  action,
  parentAction,
}) => {
  const [form] = Form.useForm();
  const [option, setOption] = useState(getInitialOption(action, parentAction));

  const options = useMemo(() => {
    if (action === "problema") {
      if (parentAction === "iniciar") {
        return [
          {
            value: "punto_recogida",
            label: "Ubicación No Válida para Iniciar",
          },
          {
            value: "sin_senal",
            label: "Sin Conectividad Para Iniciar",
          },
        ];
      } else if (parentAction === "finalizar") {
        return [
          {
            value: "punto_destino",
            label: "Ubicación No Válida para Finalizar",
          },
          {
            value: "sin_senal",
            label: "Sin Conectividad Para Finalizar",
          },
        ];
      }
    } else {
      return [
        {
          value: "recomendada",
          label: "Opción Recomendada (Ubicación Actual)",
        },
        ...(action === "iniciar"
          ? [
              {
                value: "punto_recogida",
                label: "Estoy en el Punto de Recogida",
              },
            ]
          : [{ value: "punto_destino", label: "En Punto de Destino" }]),
        {
          value: "sin_senal",
          label: "Sin Señal GPS/Internet",
        },
      ];
    }
  }, [action, parentAction]);

  const resetOption = () => {
    setOption(getInitialOption(action, parentAction));
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit({ option: values.option, description: values.description });
        form.resetFields();
        resetOption();
      })
      .catch((info) => {
        console.log("Validación fallida:", info);
      });
  };

  const onOptionChange = (e) => {
    const selectedOption = e.target.value;
    setOption(selectedOption);
    if (selectedOption === "recomendada") {
      form.resetFields(["description"]);
    }
  };

  /**
   * Función para determinar si se debe mostrar el campo de descripción
   */
  const shouldShowDescription = () =>
    (action !== "problema" && option !== "recomendada") ||
    (action === "problema" &&
      ["punto_recogida", "punto_destino", "sin_senal"].includes(option));

  return (
    <Modal
      open={open}
      title={title}
      onCancel={() => {
        form.resetFields();
        resetOption();
        onCancel();
      }}
      onOk={handleOk}
      okText="Confirmar"
      cancelText="Cancelar"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ option: getInitialOption(action, parentAction) }}
      >
        <Form.Item
          name="option"
          label="Selecciona una opción"
          rules={[
            { required: true, message: "Por favor, selecciona una opción." },
          ]}
        >
          <Radio.Group onChange={onOptionChange}>
            {options.map((opt) => (
              <Radio key={opt.value} value={opt.value}>
                {opt.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        {shouldShowDescription() && (
          <Form.Item
            name="description"
            label="Descripción del Problema"
            rules={[
              {
                required: true,
                message: "Por favor, ingresa una descripción del problema.",
              },
            ]}
          >
            <Input.TextArea rows={4} placeholder="Describe el problema..." />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

OptionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  action: PropTypes.oneOf(["iniciar", "finalizar", "problema"]).isRequired,
  parentAction: PropTypes.oneOf(["iniciar", "finalizar"]),
};

export default OptionModal;
